import React from 'react';
import PropTypes from 'prop-types';
import Skeleton from './Skeleton';
import SkeletonGroup from './SkeletonGroup';
import './Skeleton.scss';

/**
 * CardSkeleton component - Use for cards, tiles, previews.
 *
 * This component creates a skeleton layout that mimics typical card structures
 * with an optional image placeholder, title line, subtitle line, and optional
 * content lines. It's perfect for loading states of product cards, article previews,
 * dashboard widgets, or any card-based UI.
 *
 * @component
 * @example
 * // Basic card skeleton
 * <CardSkeleton />
 *
 * @example
 * // Card with image
 * <CardSkeleton showImage={true} imageHeight="120px" />
 *
 * @example
 * // Card with more content lines
 * <CardSkeleton
 *   showImage={true}
 *   imageHeight="150px"
 *   contentLines={3}
 *   spacing="md"
 * />
 *
 * @example
 * // Minimal card (no image, no content)
 * <CardSkeleton showImage={false} contentLines={0} />
 */
// Design token defaults (matching _variables.scss)
const CARD_DEFAULTS = {
  imageHeight: '100px', // $skeleton-image-height-md
  titleHeight: '18px', // $skeleton-text-height-xl
  subtitleHeight: '14px', // $skeleton-text-height-md
  contentLineHeight: '12px', // $skeleton-text-height-sm
  borderRadius: '4px', // $skeleton-border-radius-sm
  imageBorderRadius: '8px', // $skeleton-border-radius-md
};

const CardSkeleton = ({
  showImage = true,
  imageHeight = CARD_DEFAULTS.imageHeight,
  contentLines = 2,
  spacing = 'sm',
  className = '',
  ...props
}) => {
  const image = showImage ? (
    <Skeleton
      width='100%'
      height={imageHeight}
      borderRadius={CARD_DEFAULTS.imageBorderRadius}
      className='card-skeleton-image'
    />
  ) : null;

  const title = (
    <Skeleton
      width='80%'
      height={CARD_DEFAULTS.titleHeight}
      borderRadius={CARD_DEFAULTS.borderRadius}
      className='card-skeleton-title'
    />
  );

  const subtitle = (
    <Skeleton
      width='60%'
      height={CARD_DEFAULTS.subtitleHeight}
      borderRadius={CARD_DEFAULTS.borderRadius}
      className='card-skeleton-subtitle'
    />
  );

  const content =
    contentLines > 0 ? (
      <SkeletonGroup direction='vertical' spacing='xs'>
        {Array.from({ length: contentLines }, (_, index) => (
          <Skeleton
            key={index}
            width={index === contentLines - 1 ? '70%' : '100%'}
            height={CARD_DEFAULTS.contentLineHeight}
            borderRadius={CARD_DEFAULTS.borderRadius}
            className='card-skeleton-content-line'
          />
        ))}
      </SkeletonGroup>
    ) : null;

  return (
    <SkeletonGroup
      direction='vertical'
      spacing={spacing}
      className={`card-skeleton ${className}`}
      {...props}
    >
      {image}
      {title}
      {subtitle}
      {content}
    </SkeletonGroup>
  );
};

CardSkeleton.propTypes = {
  /** Whether to show an image placeholder */
  showImage: PropTypes.bool,

  /** Height of the image placeholder */
  imageHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Number of content text lines */
  contentLines: PropTypes.number,

  /** Spacing between elements (xs, sm, md, lg, xl) */
  spacing: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),

  /** Additional CSS classes */
  className: PropTypes.string,
};

export default CardSkeleton;
